// Shared aspect-ratio-lock predicate for the Konva transformer.
//
// Some object types carry a bitmap whose proportions are part of their meaning:
// a stretched photo and a squashed smiley both read as a defect, never as a
// deliberate size. Those types are resized proportionally; everything else stays
// free to be resized in a single axis.
//
// The rule lives here — and not inline at the transformer call sites — because
// it is asked in two places (hover-corner preview and selection resize) that
// must never drift apart. See O2D.W-PRD-042 §TR7.

import type { BoardObject } from '../interfaces/board-object.interface.js';

/**
 * True when `type` must keep its aspect ratio while being resized.
 *
 * The parameter is typed as the `BoardObject` union rather than `string` on
 * purpose: a renamed or removed type literal then fails to compile here instead
 * of silently turning into a permanent `false`.
 */
export function isRatioLocked(type: BoardObject['type']): boolean {
  return type === 'smiley' || type === 'image';
}

/**
 * Darf diese Auswahl einachsig (nur Breite ODER nur Höhe) skaliert werden?
 *
 * Anke, 2026-07-22 — Kanten-Anfasser auch bei Mehrfachauswahl und Gruppen, aber
 * nur wenn nichts darin Schaden nimmt. Zwei Sorten nehmen Schaden:
 *
 *  - 1:1-gesperrte Typen (Bild, Smiley). Konva legt bei mehreren Knoten EINE
 *    Verformung über alle; ein einzelnes Kind lässt sich nicht ausnehmen. Ein
 *    Bild in der Auswahl würde also mitgequetscht — genau das, wovor die Sperre
 *    es einzeln bewahrt.
 *  - Linien. Ihre Strichstärke wird beim Skalieren aus dem MITTEL beider Achsen
 *    gerechnet (`#foldLineScale`). Bei einachsigem Ziehen würde sie sich um die
 *    Hälfte des Faktors ändern, obwohl die Linie gar nicht dicker werden soll.
 *    Eine alte Vereinfachung, die nie auffiel, weil Gruppen bisher immer
 *    proportional skaliert wurden.
 *
 * Die Ecken bleiben davon unberührt: Konva fragt `keepRatio` NUR in den
 * Ecken-Zweigen ab, die Mittelanker verschieben ohnehin nur eine Koordinate.
 * Die proportionale Gruppen-Skalierung auf den Ecken bleibt also, wie sie war.
 */
export function selectionAllowsAxisResize(
  types: ReadonlyArray<BoardObject['type']>,
): boolean {
  return types.length > 0 && types.every((t) => !isRatioLocked(t) && t !== 'line');
}
